import { useNextgenRouter, AccountType } from '@nextgen-web-framework/all';
import type { Session } from '@nextgen-web-framework/all';
import { DateFormats } from '@nextgen-web-framework/core';
import {
  AccountDetailRenewalLifecycleEnum,
  AccountDetailRenewalStatusEnum,
  AccountLifeCycleStatusType,
  DynamicObjectInterface,
  Status,
} from '@commerce-profile-webcomponents/sdk';
import { processLifeCycleData, extractLifeCycleConfig } from './accountLifeCycleUtils';
import { getMarketLocale, getCookieData, formatDateByLocale } from './helper';
import { useMyAccountProfileConfig } from './masterdata';
import { getConfigByPK, MasterConfigNameConst } from './myAccountProfileUtils';
import { ProfileGeneralConfigType } from './types/propTypes';

export const useLocale = (): string => {
  try {
    const nextRouter = useNextgenRouter();
    const defaultLocale = getMarketLocale();
    const currentLocale = nextRouter?.params?.locale || defaultLocale;
    return currentLocale as string;
  } catch (_) {
    return 'en-us';
  }
};

export const useAccountRenewal = (): AccountLifeCycleStatusType => {
  const session = getCookieData(); // original use useSession hook
  const {
    id,
    type,
    aboNum,
    accountExpirationDate = '',
    renewalStatus,
    renewalLifecycle,
    accounts,
  } = session as Session;
  const account = accounts?.find((account) => String(account.accountNumber) === String(aboNum));
  const { accountStatus } = account || { accountStatus: null };

  const locale = useLocale();
  const [shortLocaleCode] = String(locale).split('-');
  const { defaultDateFormat } = useGeneralConfig();

  // Get masterdata
  const myAccountConfig = useMyAccountProfileConfig();
  const { inputs: lifeCycleInputConfig } = getConfigByPK(
    myAccountConfig,
    MasterConfigNameConst.STEP_ACCOUNT_LIFE_CYCLE,
  );

  const lifeCycleConfig = extractLifeCycleConfig(lifeCycleInputConfig, type as AccountType);

  if (id) {
    return processLifeCycleData({
      type: type as AccountType,
      accountExpirationDate,
      renewalStatus: renewalStatus as AccountDetailRenewalStatusEnum,
      renewalLifecycle: renewalLifecycle as AccountDetailRenewalLifecycleEnum,
      accountStatus: accountStatus as Status,
      locale: shortLocaleCode,
      dateFormat: defaultDateFormat,
      lifeCycleConfig,
    });
  } else {
    return {
      expiryDate: '',
      rawExpiryDate: '',
      inactiveDate: '',
      renewalStartDate: '',
      mustRenew: false,
      canRenew: false,
      canLateRenew: false,
      canUpgrade: false,
      canDowngrade: false,
      isABO: false,
      type: type as AccountType,
    };
  }
};

export const useGeneralConfig = (): ProfileGeneralConfigType => {
  const myAccountConfig = useMyAccountProfileConfig();
  const generalConfig = getConfigByPK(
    myAccountConfig,
    MasterConfigNameConst.STEP_GENERAL_CONFIG,
  ) as DynamicObjectInterface;

  return {
    ...generalConfig,
    defaultDateFormat: DateFormats.SHORT_DATE,
    // DateFormats[(generalConfig?.defaultDateFormat as keyof typeof DateFormats) ?? DateFormats.SHORT_DATE],
  } as unknown as ProfileGeneralConfigType;
};

export const useFormatDate = () => {
  const { defaultDateFormat, defaultDateLocale } = useGeneralConfig();

  const formatDate = (value: string, locale?: string): string =>
    value ? formatDateByLocale(value, locale, defaultDateFormat, defaultDateLocale) : '-';

  return { formatDate };
};
